import { inject, Injectable } from '@angular/core';
import { AuthService } from '@cnt-workspace/data-access';
import { Role } from '@curbnturf/entities';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AdminAffiliateApiService } from '../api/admin-affiliate-api.service';
import { AdminCaretakerApiService } from '../api/admin-caretaker-api.service';
import { AdminListingApiService } from '../api/admin-listing-api.service';
import { AdminPayoutApiService } from '../api/admin-payout-api.service';
import { AdminPoiApiService } from '../api/admin-poi-api.service';
import { AdminReservationApiService } from '../api/admin-reservation-api.service';
import { AttentionCounts, countAttention, NO_COUNTS } from './attention.util';

/**
 * The admin's "Needs attention" counts, shared by Home and the side menu's
 * badges. Each comes from a server count endpoint; unpublished listings use
 * one 1-row page for its total.
 */
@Injectable({ providedIn: 'root' })
export class AdminAttentionService {
  private readonly auth = inject(AuthService);
  private readonly reservations = inject(AdminReservationApiService);
  private readonly pois = inject(AdminPoiApiService);
  private readonly caretakers = inject(AdminCaretakerApiService);
  private readonly payouts = inject(AdminPayoutApiService);
  private readonly listings = inject(AdminListingApiService);
  private readonly affiliates = inject(AdminAffiliateApiService);

  private readonly _counts$ = new BehaviorSubject<AttentionCounts>(NO_COUNTS);
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  readonly counts$ = this._counts$.asObservable();
  readonly loading$ = this._loading$.asObservable();
  private inFlight?: Promise<void>;

  /**
   * For the menu badges: refresh only when the counts are missing or older
   * than `maxAgeMs`. (The layout is rebuilt per section, so the menu asks on
   * every section change; Home always refreshes.)
   */
  refreshIfStale(maxAgeMs = 5 * 60_000): Promise<void> {
    const at = this._counts$.value.updatedAt?.getTime();
    return at && Date.now() - at < maxAgeMs ? Promise.resolve() : this.refresh();
  }

  /** Reloads every count; concurrent calls share one refresh. */
  refresh(): Promise<void> {
    this.inFlight ??= this.load().finally(() => (this.inFlight = undefined));
    return this.inFlight;
  }

  private async load(): Promise<void> {
    // The menu asks as the app starts, before the session has restored; an
    // unauthenticated call would just fail (401), so wait for the admin.
    const user = await this.auth.currentUserOnceLoaded();
    if (user?.role !== Role.admin) return;
    this._loading$.next(true);
    const [reservations, pois, caretakers, payouts, unpublished, affiliates] = await Promise.allSettled([
      firstValueFrom(this.reservations.counts()),
      firstValueFrom(this.pois.pendingCount()),
      firstValueFrom(this.caretakers.count()),
      firstValueFrom(this.payouts.summary('pending')),
      firstValueFrom(this.listings.list({ available: false, deleted: false }, 1, 1)),
      firstValueFrom(this.affiliates.owedCount()),
    ]);
    this._counts$.next(countAttention({ reservations, pois, caretakers, payouts, unpublished, affiliates }));
    this._loading$.next(false);
  }
}
